import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import Lenis from "lenis";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, CloudOff, PackageOpen, SearchX } from "lucide-react";
import api from "@/lib/api";
import Hero from "@/components/public/Hero";
import StorefrontNav from "@/components/public/StorefrontNav";
import ProductCard from "@/components/public/ProductCard";

function HeroSkeleton() {
  return (
    <div className="animate-pulse">
      <div className="h-20 w-20 rounded-3xl bg-zinc-200" />
      <div className="mt-8 h-3 w-40 rounded-full bg-zinc-200" />
      <div className="mt-4 h-12 w-48 rounded-2xl bg-zinc-200" />
      <div className="mt-2 h-12 w-36 rounded-2xl bg-zinc-200" />
      <div className="mt-5 h-4 w-64 rounded-full bg-zinc-200" />
    </div>
  );
}

function ProductSkeleton() {
  return (
    <div className="overflow-hidden rounded-3xl border border-black/5 bg-white">
      <div className="aspect-[4/3] animate-pulse bg-zinc-100" />
      <div className="space-y-3 p-5">
        <div className="h-4 w-3/4 animate-pulse rounded-full bg-zinc-100" />
        <div className="h-5 w-1/3 animate-pulse rounded-full bg-zinc-100" />
        <div className="h-11 w-full animate-pulse rounded-full bg-zinc-100" />
      </div>
    </div>
  );
}

function MarqueeStrip() {
  return (
    <div className="select-none py-5 sm:py-8" aria-hidden>
      <Marquee speed={28} gradient={false}>
        {Array.from({ length: 6 }).map((_, i) => (
          <span key={i} className="mx-6 font-heading text-4xl font-semibold tracking-tighter text-black/10 sm:text-7xl">
            TECH I RECOMMEND <span className="text-black/20">&bull;</span>
          </span>
        ))}
      </Marquee>
    </div>
  );
}

export default function PublicPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading");
  const [query, setQuery] = useState("");
  const reduceMotion = useReducedMotion();
  const activeSlug = slug || "all";

  useEffect(() => {
    if (reduceMotion) return;
    const lenis = new Lenis({ lerp: 0.12, smoothWheel: true });
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, [reduceMotion]);

  const load = () => {
    setStatus("loading");
    api
      .get("/public/bootstrap")
      .then((res) => {
        setData(res.data);
        setStatus("ready");
      })
      .catch(() => setStatus("error"));
  };

  useEffect(load, []);

  useEffect(() => {
    if (sessionStorage.getItem("ti_visited")) return;
    sessionStorage.setItem("ti_visited", "1");
    api.post("/track/visit", { referrer: document.referrer, path: window.location.pathname }).catch(() => {});
  }, []);

  useEffect(() => {
    if (data?.settings?.page_title) {
      document.title = `${data.settings.page_title} — Tech I recommend, all in one place.`;
    }
  }, [data]);

  const products = data?.products || [];
  const categories = data?.categories || [];
  const settings = data?.settings || {};
  const catById = useMemo(() => Object.fromEntries(categories.map((c) => [c.id, c])), [categories]);

  const visible = useMemo(() => {
    let list = products;
    if (activeSlug !== "all") {
      list = list.filter((p) => catById[p.category_id]?.slug === activeSlug);
    }
    const q = query.trim().toLowerCase();
    if (q) {
      list = list.filter((p) => p.title.toLowerCase().includes(q));
    }
    return list;
  }, [products, activeSlug, query, catById]);

  const handleSelectCategory = (s) => {
    navigate(s === "all" ? "/" : `/category/${s}`);
  };

  const handleBuy = (p) => {
    api.post("/track/click", { product_id: p.id, referrer: document.referrer }).catch(() => {});
    window.open(p.affiliate_url, "_blank", "noopener,noreferrer");
  };

  const searching = query.trim().length > 0;

  return (
    <div className="min-h-screen bg-[#FBFBFB] text-[#111111]" data-testid="storefront-page">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:grid lg:grid-cols-12 lg:gap-16">
        <aside className="relative lg:col-span-5">
          <div className="flex flex-col justify-center py-12 sm:py-16 lg:sticky lg:top-0 lg:min-h-screen lg:py-0">
            {status === "loading" ? <HeroSkeleton /> : <Hero settings={settings} />}
            <motion.ol
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1, duration: 0.8 }}
              className="mt-12 hidden space-y-2.5 font-mono text-xs text-zinc-400 lg:block"
            >
              <li>
                <span className="text-zinc-300">01</span> &mdash; Latest picks
              </li>
              <li>
                <span className="text-zinc-300">02</span> &mdash; Follow along
              </li>
            </motion.ol>
          </div>
        </aside>

        <main className="lg:col-span-7">
          <MarqueeStrip />

          <StorefrontNav categories={categories} activeSlug={activeSlug} onSelect={handleSelectCategory} query={query} onQuery={setQuery} />

          <section className="py-8">
            <div className="mb-6 flex items-baseline justify-between">
              <h2 className="font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-400" data-testid="picks-section-label">
                01 / My Latest Picks
              </h2>
              {status === "ready" && (
                <span className="text-xs tabular-nums text-zinc-400" data-testid="product-count">
                  {visible.length} {visible.length === 1 ? "product" : "products"}
                </span>
              )}
            </div>

            {status === "loading" && (
              <div className="grid grid-cols-2 gap-3 sm:gap-5" data-testid="products-loading">
                {Array.from({ length: 4 }).map((_, i) => (
                  <ProductSkeleton key={i} />
                ))}
              </div>
            )}

            {status === "error" && (
              <div className="flex flex-col items-center rounded-3xl border border-black/5 bg-white px-6 py-16 text-center" data-testid="storefront-error-state">
                <CloudOff className="h-8 w-8 text-zinc-300" />
                <p className="mt-4 font-heading text-lg font-medium">Couldn&rsquo;t load products</p>
                <p className="mt-1 text-sm text-zinc-500">Check your connection and try again.</p>
                <button
                  onClick={load}
                  data-testid="storefront-retry-button"
                  className="mt-6 rounded-full bg-black px-6 py-2.5 text-sm font-medium text-white transition-colors hover:bg-zinc-800"
                >
                  Retry
                </button>
              </div>
            )}

            {status === "ready" && visible.length === 0 && !searching && (
              <div className="flex flex-col items-center rounded-3xl border border-black/5 bg-white px-6 py-16 text-center" data-testid="products-empty-state">
                <PackageOpen className="h-8 w-8 text-zinc-300" />
                <p className="mt-4 font-heading text-lg font-medium">No products here yet</p>
                <p className="mt-1 text-sm text-zinc-500">New picks are on the way &mdash; check back soon.</p>
              </div>
            )}

            {status === "ready" && visible.length === 0 && searching && (
              <div className="flex flex-col items-center rounded-3xl border border-black/5 bg-white px-6 py-16 text-center" data-testid="search-empty-state">
                <SearchX className="h-8 w-8 text-zinc-300" />
                <p className="mt-4 font-heading text-lg font-medium">No matches for &ldquo;{query.trim()}&rdquo;</p>
                <p className="mt-1 text-sm text-zinc-500">Try a different name or browse the categories above.</p>
              </div>
            )}

            {status === "ready" && visible.length > 0 && (
              <div className="grid grid-cols-2 gap-3 sm:gap-5" data-testid="product-grid">
                {visible.map((p, i) => (
                  <ProductCard key={p.id} product={p} index={i} categoryName={catById[p.category_id]?.name} onBuy={handleBuy} />
                ))}
              </div>
            )}
          </section>

          <footer className="border-t border-black/5 py-12" data-testid="storefront-footer">
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-400">02 / Follow along</p>
            {settings.instagram_url && (
              <a
                href={settings.instagram_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-instagram-link"
                className="group mt-4 inline-flex items-center gap-2 font-heading text-2xl font-medium tracking-tight transition-colors hover:text-zinc-500"
              >
                Follow along on Instagram
                <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </a>
            )}
            <p className="mt-8 max-w-md text-xs leading-relaxed text-zinc-400">
              As an Amazon Associate and Flipkart Affiliate, TECH iELA earns from qualifying purchases. Prices shown may change &mdash;
              the final price on the store page applies.
            </p>
            <div className="mt-6 flex items-center justify-between text-xs text-zinc-400">
              <span>&copy; {new Date().getFullYear()} TECH iELA</span>
              <Link to="/admin" data-testid="footer-admin-link" className="transition-colors hover:text-zinc-600">
                Admin
              </Link>
            </div>
          </footer>
        </main>
      </div>
    </div>
  );
}
